import { ReactNode } from "react";

import { useSellarDistributionAvailabilityList } from "../../../../hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityList";

import { SellarDistributionTimePreferenceType } from "@sellar-io/api-v2/types";

import { useBuyerRelationshipList } from "../../../../hooks/api/v2/buyerRelationship/useBuyerRelationshipList";

import { useSellarDistributionAvailabilityDriverList } from "~/hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityDriverList";

export type DistributionEducationDialogProps = {
  active: boolean;
  initialPage: DistributionEducationDialogPage;
  buyerPostcode: string;
  supplierId: number | null;
  onDismiss: () => void;
};

export type CustomDialogContentProps = {
  image: ReactNode;
  title: string;
  subtitle: ReactNode;
  icon: ReactNode;
};

export type DeliveryTimePreferenceOption = {
  label: string;
  description: string;
  value: SellarDistributionTimePreferenceType;
};

export enum DistributionEducationDialogPage {
  EXPERT_DRAY_DELIVERY,
  COMBINED_DELIVERIES,
  FREE_TO_USE,
  FLEXIBLE_SERVICE,
  SET_DELIVERY_PREFERENCES,
}

export const deliveryTimePreferenceOptions: DeliveryTimePreferenceOption[] = [
  {
    label: "AM",
    description: "Morning delivery typically between 8am-12pm",
    value: "am",
  },
  {
    label: "Early PM",
    description: "Afternoon delivery typically between 12pm-4pm",
    value: "pm",
  },
  {
    label: "All Day",
    description: "Any time from 8am to 4pm",
    value: "allDay",
  },
];

export const useGetDistributionDeliveryDays = ({
  buyerPostcode,
}: {
  buyerPostcode: string;
}) => {
  const sellarDistributionAvailabilityListQuery =
    useSellarDistributionAvailabilityList(
      {
        query: [
          {
            type: "retailerPostcode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
      },
      {
        enabled: !!buyerPostcode,
      },
    );

  const availabilityList =
    sellarDistributionAvailabilityListQuery.data?.pages.flat() ?? [];

  const deliveryDays = availabilityList.flatMap((availability) => {
    return availability.DistributionScheduleDeliveryDays.map(({ day }) => day);
  });

  const uniqueDeliveryDays = [...new Set(deliveryDays)];

  return {
    deliveryDays: uniqueDeliveryDays,
  };
};

export const listFormatter = new Intl.ListFormat("en", {
  style: "long",
  type: "conjunction",
});

export const useGetSupplierRelationshipDeliveryTimePreference = ({
  supplierId,
}: {
  supplierId: number;
}) => {
  const buyerRelationshipQuery = useBuyerRelationshipList(
    {
      query: [
        {
          type: "supplierId",
          modifier: "EQUALS",
          value: supplierId,
        },
      ],
      limit: 1,
    },
    {
      enabled: !!supplierId,
    },
  );

  const connection =
    buyerRelationshipQuery.data?.pages[0]?.[0]?.Relationship
      .BuyerRelationshipDetails;

  return {
    timePreference:
      connection?.BuyerSellarDistributionMeta?.timePreference ?? null,
  };
};

export const useGetDistributionDeliveryDrivers = ({
  buyerPostcode,
}: {
  buyerPostcode: string;
}) => {
  const sellarDistributionAvailabilityDriverListQuery =
    useSellarDistributionAvailabilityDriverList(
      {
        query: [
          {
            type: "retailerPostcode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
      },
      {
        enabled: !!buyerPostcode,
      },
    );

  const drivers =
    sellarDistributionAvailabilityDriverListQuery.data?.pages.flat() ?? [];

  return {
    drivers,
  };
};
